import type { HTMLAttributes, ReactNode } from 'react';
import { Button } from './Button';
import { cx } from './utils';

export interface SpinnerProps extends HTMLAttributes<HTMLSpanElement> {
  label?: string;
}

export function Spinner({ className, label, ...props }: SpinnerProps) {
  return (
    <span
      className={cx('ui-spinner', className)}
      role={label ? 'status' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      {...props}
    />
  );
}

export interface SkeletonProps extends HTMLAttributes<HTMLDivElement> {
  rows?: number;
}

export function Skeleton({ className, rows = 1, ...props }: SkeletonProps) {
  return (
    <div className={cx('ui-skeleton-stack', className)} aria-hidden="true" {...props}>
      {Array.from({ length: rows }).map((_, index) => (
        <span key={index} className="ui-skeleton" />
      ))}
    </div>
  );
}

export interface ProgressBarProps extends HTMLAttributes<HTMLDivElement> {
  value: number;
  max?: number;
  label?: ReactNode;
  valueLabel?: string;
  tone?: 'accent' | 'success' | 'warning' | 'danger';
  showValue?: boolean;
}

export function ProgressBar({
  className,
  value,
  max = 100,
  label,
  valueLabel,
  tone = 'accent',
  showValue = false,
  ...props
}: ProgressBarProps) {
  const safeMax = max > 0 ? max : 100;
  const clampedValue = Math.min(Math.max(value, 0), safeMax);
  const percent = Math.round((clampedValue / safeMax) * 100);
  const displayValue = valueLabel ?? `${percent}%`;

  return (
    <div className={cx('ui-progress', `ui-progress--${tone}`, className)} {...props}>
      {(label || showValue) ? (
        <div className="ui-progress__header">
          {label ? <span>{label}</span> : <span />}
          {showValue ? <span>{displayValue}</span> : null}
        </div>
      ) : null}
      <div
        className="ui-progress__track"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={safeMax}
        aria-valuenow={clampedValue}
        aria-valuetext={displayValue}
      >
        <span className="ui-progress__fill" style={{ inlineSize: `${percent}%` }} />
      </div>
    </div>
  );
}

export interface PaginationProps {
  page: number;
  pageCount: number;
  onPrevious?: () => void;
  onNext?: () => void;
  previousLabel: string;
  nextLabel: string;
  pageLabel: string;
}

export function Pagination({
  page,
  pageCount,
  onPrevious,
  onNext,
  previousLabel,
  nextLabel,
  pageLabel,
}: PaginationProps) {
  return (
    <nav className="ui-pagination" aria-label={pageLabel}>
      <Button variant="secondary" size="sm" onClick={onPrevious} disabled={page <= 1}>
        {previousLabel}
      </Button>
      <span className="ui-pagination__status">
        {page} / {pageCount}
      </span>
      <Button variant="secondary" size="sm" onClick={onNext} disabled={page >= pageCount}>
        {nextLabel}
      </Button>
    </nav>
  );
}

export interface ToastProps {
  title: ReactNode;
  children?: ReactNode;
  tone?: 'info' | 'success' | 'warning' | 'danger';
  closeLabel?: string;
  onClose?: () => void;
}

export function Toast({ title, children, tone = 'info', closeLabel, onClose }: ToastProps) {
  return (
    <div className={cx('ui-toast', `ui-toast--${tone}`)} role="status">
      <div className="ui-toast__content">
        <strong className="ui-toast__title">{title}</strong>
        {children ? <div className="ui-toast__body">{children}</div> : null}
      </div>
      {onClose && closeLabel ? (
        <button className="ui-toast__close" type="button" aria-label={closeLabel} onClick={onClose}>
          x
        </button>
      ) : null}
    </div>
  );
}
